(ns frontend.view.modals.account-invite.view
  (:require
   [clojure.string :as string]
   [common.email :as email]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.account-invite.events :as events]
   [frontend.view.modals.components.processing-modal :as processing-modal]
   [ui.components.button.view :as button]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.components.radio-group.view :as radio-group]
   [ui.editors.labelled-input.view :as labelled-input]))

(defonce modal-key :account-invite-modal)

(defn not-unique-email?
  [existing-accounts email]
  (let [lc (fu/trimmed-lower-case email)]
    (contains? (->> existing-accounts
                    (map :account/email)
                    (map fu/trimmed-lower-case)
                    (set))
               lc)))

(defn not-primary-tenant-domain-info
  [on-confirm! on-cancel!]
  [:div
   {:class "p-3 flex flex gap-3 bg-danger-light border border-solid border-danger-light-border rounded"}
   [:div
    [:i
     {:class-name "fas fa-exclamation-triangle text-xl text-danger-light-text-paler"}]]
   [:div
    {:class "flex flex-col gap-4"}
    [:div
     [:div
      {:class "font-bold"}
      (loc/tr :not-your-company-primary-domain)]
     [:div
      {:class "text-xs"}
      (loc/tr :invite-user-email-confirmation)]]
    [:div
     {:class "flex gap-2"}
     [button/sm-danger-solid
      {:on-click on-confirm!}
      (loc/tr :email-not-primary-domain-address-confirm)]
     [button/sm-secondary-hollow
      {:on-click on-cancel!}
      (loc/tr :email-not-primary-domain-address-cancel)]]]])

(defn view-body
  [state _common-input-params existing-accounts _show-info-texts?]
  (let [tenant-primary-domain (cu/str-trim-lcase (:tenant/domain-primary @session/tenant))
        confirm-custom-email-domain! (fn []
                                       (swap! state (fn [state]
                                                      (-> state
                                                          (dissoc :show-confirmation-infobox?)
                                                          (assoc :confirmed? true
                                                                 :info-text (loc/tr :email-does-not-have-the-primary-domain-address)
                                                                 :email-field-dom-key (str (gensym "email-input-key")))))))
        {:keys [_email _info-text]} @state
        clear-email! (fn []
                       (swap! state (fn [state]
                                      (-> state
                                          (dissoc :show-confirmation-infobox? :email :info-text :confimed?)
                                          (assoc :email-field-dom-key (str (gensym "email-input-key")))))))
        options (->> [:role/user :role/admin]
                     (map (fn [p]
                            {:value   p
                             :content (loc/tr p)})))]
    [:div
     {:class "flex flex-col gap-4"}
     [:div
      [labelled-input/view
       {:label      (loc/tr :tenant-users/first-name)
        :mandatory? true
        :data-type  :data.type/text-simple
        :value      (get @state :first-name)
        :on-blur    (fn [new-value]
                      (let [empty-name? (string/blank? new-value)
                            err (cond empty-name?
                                      (loc/tr :error-field-not-clearable [(loc/tr :first-name)])
                                      :else nil)]
                        (swap! state assoc-in [:error-message :first-name] err)))
        :on-change  (fn [new-value]
                      (swap! state assoc :first-name new-value))}]
      [:p {:class "text-xs text-danger-light-text"}
       (get-in @state [:error-message :first-name])]]
     [:div
      [labelled-input/view
       {:label      (loc/tr :tenant-users/last-name)
        :mandatory? true
        :data-type  :data.type/text-simple
        :value      (get @state :last-name)
        :on-blur    (fn [new-value]
                      (let [empty-name? (string/blank? new-value)
                            err (cond empty-name?
                                      (loc/tr :error-field-not-clearable [(loc/tr :last-name)])
                                      :else nil)]
                        (swap! state assoc-in [:error-message :last-name] err)))
        :on-change  (fn [new-value]
                      (swap! state assoc :last-name new-value))}]
      [:p {:class "text-xs text-danger-light-text"}
       (get-in @state [:error-message :last-name])]]
     ^{:key (str "account-invitation-email-" (:email-field-dom-key @state))}
     [:div
      [labelled-input/view
       {:label      (loc/tr :tenant-users/email)
        :mandatory? true
        :data-type  :data.type/text-simple
        :value      (get @state :email)
        :on-blur    (fn [new-value]
                      (js/console.log "Blur : " new-value @state)
                      (let [valid-email? (email/valid-email? new-value)
                            trimmed-lc-email (cu/str-trim-lcase new-value)
                            empty-email? (empty? trimmed-lc-email)
                            not-unique-email? (not-unique-email? existing-accounts new-value)
                            from-other-domain? (not (string/ends-with? trimmed-lc-email tenant-primary-domain))
                            domain-confirmed? (:confirmed? @state)]
                        (swap! state (fn [state]
                                       (-> state
                                           (assoc :email new-value)
                                           (dissoc :info-text :confirmed?))))
                        (js/console.log "Blur : " from-other-domain? " << " valid-email? " << " not-unique-email?)
                        (if (and (not empty-email?)
                                 valid-email?
                                 from-other-domain?
                                 (not not-unique-email?))
                          (swap! state assoc :show-confirmation-infobox? true)
                          (swap! state dissoc :show-confirmation-infobox? :confirmed?))
                        (let [err (cond empty-email?
                                        (loc/tr :error-field-not-clearable [(loc/tr :email)])
                                        (not valid-email?)
                                        (loc/tr :account-email-not-valid)
                                        not-unique-email?
                                        (loc/tr :this-user-already-exists)
                                        (and (not domain-confirmed?) from-other-domain?)
                                        (loc/tr :email-does-not-have-the-primary-domain-address)
                                        :else nil)]
                          (swap! state assoc-in [:error-message :email] err))))
        :on-change  (fn [new-value]
                      (swap! state assoc :email new-value))}]
      [:p {:class "text-xs text-danger-light-text"}
       (get-in @state [:error-message :email])]]

     (when (:show-confirmation-infobox? @state)
       [:div
        {:class "relative"}
        [not-primary-tenant-domain-info confirm-custom-email-domain! clear-email!]])

     [:div
      [:div
       {:class "pb-2 text-input-light-text font-semibold"}
       [:span
        {:class "font-bold"}
        (loc/tr :role) false]]
      [radio-group/radio-group-v2
       {:checked     (:role-kw @state)
        :orientation :vertical
        :on-change   (fn [value]
                       (swap! state assoc :role-kw value))}
       options]]

     [:div
      {:class "inline-flex gap-3"}
      [:label
       {:class "font-semibold flex flex-row gap-3 cursor-pointer"}
       [checkbox/view
        {:value     (:send-email? @state)
         :on-change (fn [new-value]
                      (swap! state assoc :send-email? (boolean new-value)))}]
       (loc/tr :invite-new-user)]]]))

(defn footer-view
  [state duplicate-email? on-close! on-account-invited]
  (let [{:keys [send-email? confirmed? email]} @state
        tenant-primary-domain (cu/str-trim-lcase (:tenant/domain-primary @session/tenant))
        trimmed-lc-email (cu/str-trim-lcase email)
        primary-email-domain? (string/ends-with? trimmed-lc-email tenant-primary-domain)
        primary-or-confirmed? (or confirmed?
                                  primary-email-domain?)
        button-disabled? (or duplicate-email?
                             (not (events/form-ready? state))
                             (not primary-or-confirmed?))]
    (js/console.log "DIsabled? " duplicate-email? " > " (not (events/form-ready? state))
                    " > " (not primary-or-confirmed?)
                    " << " primary-email-domain?
                    " << " (not (events/form-ready? state)))
    [modal/right-single-button
     nil
     button-disabled?
     #(events/invite-account! state on-close! on-account-invited)
     (if send-email?
       (loc/tr :invite-user)
       (loc/tr :create-account))
     nil]))

(defn view
  [{:keys [shown? close-modal! props state class-name topmost?]}]
  (let [{:keys [existing-accounts init-email init-first-name init-last-name
                on-account-invited show-info-texts?]}
        props
        duplicate-email? (not-unique-email? existing-accounts (:email @state))
        on-close! (fn []
                    (close-modal!))
        common-input-params {:type        :text
                             :entity      @state
                             :label       (fn [args]
                                            (loc/tr (:attribute args)))
                             :mandatory?  true
                             :entity-atom state
                             :tools       []
                             :validate!   (fn [attribute val]
                                            (swap! state assoc attribute val)
                                            (cond (empty? val)
                                                  (loc/tr :error-field-not-clearable [(loc/tr attribute)])))
                             :on-save!    (fn [attribute new-value on-success! _on-error!]
                                            (swap! state assoc attribute new-value)
                                            (when (fn? on-success!)
                                              (on-success!)))}]
    [processing-modal/render-modal
     {:state             state
      :shown?            (and shown? topmost?)
      :success-text      (if (:send-email? @state)
                           (loc/tr :invitation-sent-successfully)
                           (loc/tr :account-successfully-created))
      :on-close!         on-close!
      :class-name        class-name
      :on-show!          (fn []
                           (when init-email
                             (swap! state assoc :email init-email))
                           (when init-first-name
                             (swap! state assoc :first-name init-first-name))
                           (when init-last-name
                             (swap! state assoc :last-name init-last-name))
                           (swap! state assoc
                                  :role-kw :role/user
                                  :send-email? true)
                           (deref state))
      :default-title-fn  #(loc/tr :invite-user)
      :default-body-fn   (partial view-body state common-input-params existing-accounts show-info-texts?)
      :default-footer-fn (partial footer-view state duplicate-email? on-close! on-account-invited)}]))
